import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  CheckSquare, Phone, Users as UsersIcon, Clock, Mail, ArrowRight
} from 'lucide-react';
import api from '../services/api';

const TYPE_META = {
  call:     { Icon: Phone,       color: 'text-green-600'  },
  meeting:  { Icon: UsersIcon,   color: 'text-blue-600'   },
  todo:     { Icon: CheckSquare, color: 'text-gray-600'   },
  deadline: { Icon: Clock,       color: 'text-red-600'    },
  email:    { Icon: Mail,        color: 'text-purple-600' }
};

const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };

const DashboardTasksWidget = () => {
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading } = useQuery(
    ['tasks', 'dashboard'],
    () => api.get('/tasks', { params: { assignedTo: 'me', completed: 'false' } }).then((r) => r.data)
  );

  const patch = useMutation(
    ({ id, patch }) => api.patch(`/tasks/${id}`, patch).then((r) => r.data),
    { onSuccess: () => queryClient.invalidateQueries(['tasks']) }
  );

  const now = startOfDay(new Date()).getTime();
  const weekEnd = now + 6 * 24 * 3600 * 1000;
  const monthEnd = now + 30 * 24 * 3600 * 1000;

  const today = tasks.filter((t) => t.dueDate && startOfDay(new Date(t.dueDate)).getTime() === now);
  const overdue = tasks.filter((t) => t.dueDate && new Date(t.dueDate).getTime() < now);
  const upcoming = tasks.filter((t) => {
    if (!t.dueDate) return false;
    const due = startOfDay(new Date(t.dueDate)).getTime();
    return due > now && due <= weekEnd;
  });
  const thisMonth = tasks.filter((t) => {
    if (!t.dueDate) return false;
    const due = startOfDay(new Date(t.dueDate)).getTime();
    return due > weekEnd && due <= monthEnd;
  });

  const displayItems = [...overdue, ...today, ...upcoming, ...thisMonth].slice(0, 6);

  return (
    <div className="card mb-8">
      <div className="card-header flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CheckSquare size={18} className="mr-2" />
            My Tasks
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Overdue · today · this week · this month
          </p>
        </div>
        <Link
          to="/tasks"
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          View all <ArrowRight size={14} className="ml-1" />
        </Link>
      </div>
      <div className="card-body">
        {isLoading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : displayItems.length === 0 ? (
          <p className="text-gray-400 text-sm italic">No tasks due this month. Time to plan!</p>
        ) : (
          <div className="space-y-2">
            {overdue.length > 0 && (
              <div className="text-xs font-bold text-red-900 uppercase tracking-wider px-1">
                Overdue ({overdue.length})
              </div>
            )}
            {overdue.slice(0, 3).map((t) => <Row key={t._id} task={t} onToggle={(id) => patch.mutate({ id, patch: { completed: true } })} accent="border-red-300 bg-red-100" />)}

            {today.length > 0 && (
              <div className="text-xs font-semibold text-red-700 uppercase tracking-wider px-1 mt-3">
                Today ({today.length})
              </div>
            )}
            {today.slice(0, 3).map((t) => <Row key={t._id} task={t} onToggle={(id) => patch.mutate({ id, patch: { completed: true } })} accent="border-red-200 bg-red-50" />)}

            {overdue.length === 0 && today.length === 0 && upcoming.length > 0 && (
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider px-1">
                This Week ({upcoming.length})
              </div>
            )}
            {overdue.length === 0 && today.length === 0 && upcoming.slice(0, 5).map((t) => <Row key={t._id} task={t} onToggle={(id) => patch.mutate({ id, patch: { completed: true } })} accent="border-amber-200 bg-amber-50" />)}

            {overdue.length === 0 && today.length === 0 && upcoming.length === 0 && thisMonth.length > 0 && (
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider px-1">
                This Month ({thisMonth.length})
              </div>
            )}
            {overdue.length === 0 && today.length === 0 && upcoming.length === 0 && thisMonth.slice(0, 5).map((t) => <Row key={t._id} task={t} onToggle={(id) => patch.mutate({ id, patch: { completed: true } })} accent="border-emerald-200 bg-emerald-50" />)}
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ task, onToggle, accent }) => {
  const meta = TYPE_META[task.type] || TYPE_META.todo;
  const { Icon } = meta;
  return (
    <div className={`flex items-center gap-2 p-2 rounded-md border ${accent}`}>
      <input type="checkbox" onChange={() => onToggle(task._id)} />
      <Icon size={14} className={meta.color} />
      <span className="text-sm text-gray-900 flex-1 truncate">{task.title}</span>
      {task.leadId && (
        <Link to={`/leads/${task.leadId._id}`} className="text-[11px] text-primary-600 hover:underline flex-shrink-0">
          {task.leadId.firstName} {task.leadId.lastName}
        </Link>
      )}
      {task.dueDate && (
        <span className="text-[11px] text-gray-500 flex-shrink-0">
          {new Date(task.dueDate).toLocaleDateString([], { month: 'short', day: 'numeric' })}
        </span>
      )}
    </div>
  );
};

export default DashboardTasksWidget;
